import {
  BrandOrderRequirementType,
  PurchaseTermsNamedItemType,
  RuleGroupConfigProductType,
  RuleGroupConfigType,
  RuleGroupInactiveFilterItemType,
  RuleGroupInactiveFiltersType,
  RuleGroupLevelEnum,
  RuleGroupType,
  SkuOrderRequirementType,
} from 'features/Enterprise/Vendor/types/PurchaseTerms'
import { VendorDetailsInterface } from 'features/Enterprise/Vendor/types/VendorManagement'
import { hydrateBrandOrderRequirementScope } from 'features/Enterprise/Vendor/pages/RegularVendor/FormVendor/Shipping/ShippingFormDrawer/PurchaseTerms/BrandTerms/helpers/resolveNamesFromCatalog'
import {
  resolveMoqMultiplierForVendor,
  resolveMoqMultiplierSubmitValue,
} from 'features/Enterprise/Vendor/pages/RegularVendor/FormVendor/Shipping/ShippingFormDrawer/PurchaseTerms/utils/moqMultiplierToggle'
import { sanitizeMoqMovFieldsByOrderRequirement } from 'features/Enterprise/Vendor/pages/RegularVendor/FormVendor/Shipping/ShippingFormDrawer/PurchaseTerms/utils/orderRequirementFields'
import { resolveMoqUomSelectValue } from 'features/Enterprise/Vendor/pages/RegularVendor/FormVendor/Shipping/ShippingFormDrawer/PurchaseTerms/utils/moqUom'
import { filterActiveBrandScopeConfigs } from 'features/Enterprise/Vendor/services/purchaseTerms/buildRuleConfigsQueryParam'

export type ShippingInfoWithRuleGroupsType = VendorDetailsInterface['shipping_information'][0]

export type ShippingInfoRuleGroupSourceType = {
  id?: number
  order_requirement?: string
  moq_qty?: number
  moq_multiplier?: number
  moq_uom?: string
  mov?: string
  shipping_fee_rules?: boolean
  shipping_fee_amount?: string
  moq_max?: boolean
  use_rule_groups?: boolean
  rule_groups?: RuleGroupType[]
  brand_order_requirements?: BrandOrderRequirementType[]
  sku_order_requirements?: SkuOrderRequirementType[]
}

const EMPTY_BRAND_REQUIREMENTS: BrandOrderRequirementType[] = []
const EMPTY_SKU_REQUIREMENTS: SkuOrderRequirementType[] = []

const hasValidBrandConfig = (configs?: RuleGroupConfigType[]) =>
  configs?.some(
    (config) =>
      (config.brand_id ?? 0) > 0 ||
      (config.l1_category_id ?? 0) > 0 ||
      (config.l2_category_id ?? 0) > 0,
  ) ?? false

const hasValidSkuProduct = (products?: RuleGroupConfigProductType[]) =>
  products?.some((product) => (product.product_id ?? 0) > 0) ?? false

const hasVisibleBrandRequirements = (requirements: BrandOrderRequirementType[]) =>
  requirements.some(
    (row) =>
      !row.marked_for_deletion &&
      ((row.brands?.some((brand) => brand.id > 0) ?? false) ||
        (row.l1Categories?.some((category) => (category.id ?? 0) > 0) ?? false) ||
        (row.l2Categories?.some((category) => (category.id ?? 0) > 0) ?? false) ||
        hasValidBrandConfig(row.rule_group_configs)),
  )

const hasVisibleSkuRequirements = (requirements: SkuOrderRequirementType[]) =>
  requirements.some(
    (row) =>
      !row.marked_for_deletion &&
      ((row.products?.some((product) => product.id > 0) ?? false) ||
        hasValidSkuProduct(row.rule_group_products)),
  )

const hasBrandScopeData = (requirements?: BrandOrderRequirementType[]) =>
  requirements?.some(
    (row) =>
      (row.brands?.some((brand) => brand.id > 0) ?? false) ||
      (row.l1Categories?.some((category) => (category.id ?? 0) > 0) ?? false) ||
      (row.l2Categories?.some((category) => (category.id ?? 0) > 0) ?? false) ||
      hasValidBrandConfig(row.rule_group_configs),
  ) ?? false

const hasSkuScopeData = (requirements?: SkuOrderRequirementType[]) =>
  requirements?.some(
    (row) =>
      (row.products?.some((product) => product.id > 0) ?? false) ||
      hasValidSkuProduct(row.rule_group_products),
  ) ?? false

const hasOnlySoftDeletedRequirements = <T extends { marked_for_deletion?: boolean }>(
  requirements: T[],
) => requirements.length > 0 && requirements.every((row) => row.marked_for_deletion)

export const filterOrderRequirementsForSubmission = <
  T extends { marked_for_deletion?: boolean; id?: number },
>(
  requirements: T[] | undefined,
): T[] => (requirements ?? []).filter((row) => !row.marked_for_deletion || (row.id ?? 0) > 0)

const applyMarkedForDeletionFromMapped = <T extends { id?: number; marked_for_deletion?: boolean }>(
  requirements: T[],
  mappedRequirements: T[],
): T[] => {
  const mappedDeletionById = new Map<number, boolean>()
  mappedRequirements.forEach((row) => {
    if (row.id && row.id > 0 && row.marked_for_deletion === true) {
      mappedDeletionById.set(row.id, true)
    }
  })

  if (mappedDeletionById.size === 0) return requirements

  return requirements.map((row) => {
    const rowId = row.id
    if (rowId && rowId > 0 && mappedDeletionById.get(rowId)) {
      return { ...row, marked_for_deletion: true }
    }
    return row
  })
}

const resolveBrandOrderRequirements = (
  legacyRequirements: BrandOrderRequirementType[],
  mappedRequirements: BrandOrderRequirementType[],
  useLegacy: boolean,
  preferLegacyFields?: boolean,
): BrandOrderRequirementType[] => {
  if (!useLegacy) return mappedRequirements

  const hydratedLegacy = hydrateBrandOrderRequirements(legacyRequirements)
  let resolved: BrandOrderRequirementType[]
  if (hasVisibleBrandRequirements(hydratedLegacy)) {
    resolved = hydratedLegacy
  } else if (preferLegacyFields && hasOnlySoftDeletedRequirements(hydratedLegacy)) {
    resolved = hydratedLegacy
  } else {
    resolved = mappedRequirements.length > 0 ? mappedRequirements : hydratedLegacy
  }

  return applyMarkedForDeletionFromMapped(resolved, mappedRequirements)
}

const resolveSkuOrderRequirements = (
  legacyRequirements: SkuOrderRequirementType[],
  mappedRequirements: SkuOrderRequirementType[],
  useLegacy: boolean,
  preferLegacyFields?: boolean,
): SkuOrderRequirementType[] => {
  if (!useLegacy) return mappedRequirements

  let resolved: SkuOrderRequirementType[]
  if (hasVisibleSkuRequirements(legacyRequirements)) {
    resolved = legacyRequirements
  } else if (preferLegacyFields && hasOnlySoftDeletedRequirements(legacyRequirements)) {
    resolved = legacyRequirements
  } else {
    resolved = mappedRequirements.length > 0 ? mappedRequirements : legacyRequirements
  }

  return applyMarkedForDeletionFromMapped(resolved, mappedRequirements)
}

const hydrateBrandOrderRequirements = (requirements: BrandOrderRequirementType[] = []) =>
  requirements.map(hydrateBrandOrderRequirementScope)

const getActiveBrandRuleGroups = (ruleGroups?: RuleGroupType[]) =>
  ruleGroups?.filter(
    (group) => group.level === RuleGroupLevelEnum.BRAND && !group.marked_for_deletion,
  ) ?? []

const getActiveSkuRuleGroups = (ruleGroups?: RuleGroupType[]) =>
  ruleGroups?.filter(
    (group) => group.level === RuleGroupLevelEnum.SKU && !group.marked_for_deletion,
  ) ?? []

const movToString = (mov: number | string | undefined | null): string => {
  if (mov === undefined || mov === null || mov === '') return '0'
  return String(mov)
}

const movToNumber = (mov: string | number | undefined | null): number => {
  if (mov === undefined || mov === null || mov === '') return 0
  const parsed = typeof mov === 'number' ? mov : Number.parseFloat(mov)
  return Number.isFinite(parsed) ? parsed : 0
}

const uniqueNamedItems = (items: PurchaseTermsNamedItemType[]): PurchaseTermsNamedItemType[] => {
  const byId = new Map<number, PurchaseTermsNamedItemType>()
  items.forEach((item) => {
    if (item.id > 0) {
      byId.set(item.id, item)
    }
  })
  return Array.from(byId.values())
}

const toNamedItem = (
  id: number | undefined,
  name: string | undefined,
): PurchaseTermsNamedItemType | null => {
  if (!id || id <= 0) return null
  return { id, name: name?.trim() || '' }
}

const collectUniqueIds = (ids: Array<number | undefined>) => {
  const uniqueIds = new Set<number>()
  ids.forEach((id) => {
    if (id && id > 0) uniqueIds.add(id)
  })
  return Array.from(uniqueIds)
}

const collectInactiveFilterIds = (filterItems: { id?: number }[] | undefined): number[] =>
  collectUniqueIds((filterItems ?? []).map((item) => item.id))

const buildAllScopeInactiveIds = (
  brands: PurchaseTermsNamedItemType[],
  l1Categories: RuleGroupInactiveFilterItemType[],
  l2Categories: RuleGroupInactiveFilterItemType[],
) => ({
  brand_ids: brands.map((brand) => brand.id),
  l1_category_ids: collectInactiveFilterIds(l1Categories),
  l2_category_ids: collectInactiveFilterIds(l2Categories),
})

const buildInactiveScopeIdsFromFilters = (
  inactiveFilters: RuleGroupInactiveFiltersType,
  brands: PurchaseTermsNamedItemType[],
  l1Categories: RuleGroupInactiveFilterItemType[],
  l2Categories: RuleGroupInactiveFilterItemType[],
  isGroupInactive: boolean,
) => {
  const brandIds = collectInactiveFilterIds(inactiveFilters.brands)
  const l1CategoryIds = collectInactiveFilterIds(inactiveFilters.l1_categories)
  const l2CategoryIds = collectInactiveFilterIds(inactiveFilters.l2_categories)

  if (brandIds.length || l1CategoryIds.length || l2CategoryIds.length) {
    return {
      brand_ids: brandIds.length ? brandIds : undefined,
      l1_category_ids: l1CategoryIds.length ? l1CategoryIds : undefined,
      l2_category_ids: l2CategoryIds.length ? l2CategoryIds : undefined,
    }
  }

  if (!isGroupInactive) return undefined
  return buildAllScopeInactiveIds(brands, l1Categories, l2Categories)
}

const buildInactiveScopeIdsFromConfigFlags = (configs: RuleGroupConfigType[]) => {
  const brandIds = collectUniqueIds(
    configs.filter((config) => config.brand_is_active === false).map((config) => config.brand_id),
  )
  const l1CategoryIds = collectUniqueIds(
    configs
      .filter((config) => config.l1_category_is_active === false)
      .map((config) => config.l1_category_id),
  )
  const l2CategoryIds = collectUniqueIds(
    configs
      .filter((config) => config.l2_category_is_active === false)
      .map((config) => config.l2_category_id),
  )

  if (!brandIds.length && !l1CategoryIds.length && !l2CategoryIds.length) {
    return undefined
  }

  return {
    brand_ids: brandIds.length ? brandIds : undefined,
    l1_category_ids: l1CategoryIds.length ? l1CategoryIds : undefined,
    l2_category_ids: l2CategoryIds.length ? l2CategoryIds : undefined,
  }
}

const buildBrandInactiveScopeIds = (
  configs: RuleGroupConfigType[],
  brands: PurchaseTermsNamedItemType[],
  l1Categories: RuleGroupInactiveFilterItemType[],
  l2Categories: RuleGroupInactiveFilterItemType[],
  isGroupInactive: boolean,
  inactiveFilters?: RuleGroupInactiveFiltersType,
) => {
  if (inactiveFilters) {
    return buildInactiveScopeIdsFromFilters(
      inactiveFilters,
      brands,
      l1Categories,
      l2Categories,
      isGroupInactive,
    )
  }

  if (isGroupInactive) {
    return buildAllScopeInactiveIds(brands, l1Categories, l2Categories)
  }

  return buildInactiveScopeIdsFromConfigFlags(configs)
}

const buildSkuInactiveProductIds = (
  products: RuleGroupConfigProductType[],
  namedProducts: PurchaseTermsNamedItemType[],
  isGroupInactive: boolean,
) => {
  if (isGroupInactive) {
    return namedProducts.map((product) => product.id)
  }

  const inactiveIds = collectUniqueIds(
    products.filter((product) => product.is_active === false).map((product) => product.product_id),
  )
  return inactiveIds.length ? inactiveIds : undefined
}

const mapBrandRuleGroupToRequirement = (group: RuleGroupType): BrandOrderRequirementType => {
  const allConfigs = group.configs ?? []
  const activeConfigs = filterActiveBrandScopeConfigs(allConfigs)
  const isGroupInactive = group.is_active === false

  const brands = uniqueNamedItems(
    activeConfigs.flatMap((config) => {
      const item = toNamedItem(config.brand_id, config.brand_name)
      return item ? [item] : []
    }),
  )
  const l1Categories = uniqueNamedItems(
    activeConfigs.flatMap((config) => {
      const item = toNamedItem(config.l1_category_id, config.l1_category_name)
      return item ? [item] : []
    }),
  )
  const l2Categories = uniqueNamedItems(
    activeConfigs.flatMap((config) => {
      const item = toNamedItem(config.l2_category_id, config.l2_category_name)
      return item ? [item] : []
    }),
  )

  return {
    id: group.id && group.id > 0 ? group.id : undefined,
    brands,
    l1Categories,
    l2Categories,
    order_requirement: group.order_requirement === 'MoV Only' ? 'MoV Only' : 'MoQ Only',
    moq_qty: group.moq_qty,
    moq_uom: group.moq_uom,
    moq_multiplier: group.moq_multiplier,
    mov: movToString(group.mov),
    notes: group.notes ?? '',
    is_inactive: isGroupInactive,
    marked_for_deletion: group.marked_for_deletion === true,
    inactive_filters: group.inactive_filters,
    inactive_scope_ids: buildBrandInactiveScopeIds(
      activeConfigs,
      brands,
      l1Categories,
      l2Categories,
      isGroupInactive,
      group.inactive_filters,
    ),
    rule_group_configs: allConfigs,
    last_validated_at: group.last_validated_at,
  }
}

export const mapRuleGroupProductToNamedItem = (
  product: RuleGroupConfigProductType,
): PurchaseTermsNamedItemType => {
  const productName = product.product_name?.trim() || ''
  const skuNumber = product.sku_number?.trim() || ''

  if (productName && skuNumber) {
    return { id: product.product_id, name: `${productName} - ${skuNumber}` }
  }
  if (skuNumber) {
    return { id: product.product_id, name: skuNumber }
  }
  return { id: product.product_id, name: productName }
}

const mapProductIdsToNamedItems = (productIds: number[]): PurchaseTermsNamedItemType[] =>
  productIds.filter((id) => id > 0).map((id) => ({ id, name: '' }))

const mapSkuRuleGroupToRequirement = (group: RuleGroupType): SkuOrderRequirementType => {
  const ruleGroupProducts = group.products ?? []
  const productsFromObjects = ruleGroupProducts.map(mapRuleGroupProductToNamedItem)
  const products = productsFromObjects.length
    ? productsFromObjects
    : mapProductIdsToNamedItems(group.product_ids ?? [])
  const isGroupInactive = group.is_active === false
  const inactiveProductIds = buildSkuInactiveProductIds(
    ruleGroupProducts,
    products,
    isGroupInactive,
  )
  const isAllProductsInactive =
    products.length > 0 && !!inactiveProductIds && inactiveProductIds.length === products.length

  return {
    id: group.id && group.id > 0 ? group.id : undefined,
    products,
    order_requirement: group.order_requirement === 'MoV Only' ? 'MoV Only' : 'MoQ Only',
    moq_qty: group.moq_qty,
    moq_uom: group.moq_uom,
    moq_multiplier: group.moq_multiplier,
    mov: movToString(group.mov),
    notes: group.notes ?? '',
    is_inactive: isGroupInactive || isAllProductsInactive,
    marked_for_deletion: group.marked_for_deletion === true,
    inactive_scope_ids: inactiveProductIds?.length
      ? { product_ids: inactiveProductIds }
      : undefined,
    rule_group_products: ruleGroupProducts,
    last_validated_at: group.last_validated_at,
  }
}

const resolveVendorRuleGroupIdFromApi = (info: { rule_groups?: RuleGroupType[] }): number => {
  const vendorGroup = info.rule_groups?.find((group) => group.level === RuleGroupLevelEnum.VENDOR)
  return vendorGroup?.id && vendorGroup.id > 0 ? vendorGroup.id : 0
}

const resolveOrderRequirement = (
  vendorGroup: RuleGroupType,
  fallback: ShippingInfoWithRuleGroupsType['order_requirement'],
): ShippingInfoWithRuleGroupsType['order_requirement'] => {
  if (vendorGroup.order_requirement === 'MoV Only') return 'MoV Only'
  if (vendorGroup.order_requirement === 'MoQ Only') return 'MoQ Only'
  return fallback
}

const applyVendorRuleGroupToLegacyFields = (
  info: ShippingInfoWithRuleGroupsType,
  vendorGroup: RuleGroupType,
): ShippingInfoWithRuleGroupsType => ({
  ...info,
  order_requirement: resolveOrderRequirement(vendorGroup, info.order_requirement),
  moq_qty: vendorGroup.moq_qty ?? info.moq_qty,
  moq_multiplier: vendorGroup.moq_multiplier ?? info.moq_multiplier,
  moq_uom: vendorGroup.moq_uom ?? info.moq_uom,
  mov: movToString(vendorGroup.mov ?? info.mov),
  shipping_fee_rules: vendorGroup.shipping_fee_rules ?? info.shipping_fee_rules,
  shipping_fee_amount: vendorGroup.shipping_fee_amount ?? info.shipping_fee_amount,
})

type WithPurchaseTermsArraysType = {
  brand_order_requirements: BrandOrderRequirementType[]
  sku_order_requirements: SkuOrderRequirementType[]
}

const withDefaultPurchaseTermsArrays = <T extends ShippingInfoWithRuleGroupsType>(
  info: T,
): T & WithPurchaseTermsArraysType => ({
  ...info,
  brand_order_requirements: hydrateBrandOrderRequirements(
    info.brand_order_requirements ?? EMPTY_BRAND_REQUIREMENTS,
  ),
  sku_order_requirements: info.sku_order_requirements ?? EMPTY_SKU_REQUIREMENTS,
})

const mapRuleGroupsToPurchaseTerms = <T extends ShippingInfoWithRuleGroupsType>(
  info: T,
): T & WithPurchaseTermsArraysType => {
  const vendorGroup = info.rule_groups?.find((group) => group.level === RuleGroupLevelEnum.VENDOR)
  const brandGroups =
    info.rule_groups?.filter((group) => group.level === RuleGroupLevelEnum.BRAND) ?? []
  const skuGroups =
    info.rule_groups?.filter((group) => group.level === RuleGroupLevelEnum.SKU) ?? []

  let normalized: T & WithPurchaseTermsArraysType = {
    ...info,
    brand_order_requirements: hydrateBrandOrderRequirements(
      brandGroups.map(mapBrandRuleGroupToRequirement),
    ),
    sku_order_requirements: skuGroups.map(mapSkuRuleGroupToRequirement),
  }

  if (vendorGroup) {
    normalized = applyVendorRuleGroupToLegacyFields(normalized, vendorGroup) as T &
      WithPurchaseTermsArraysType
  }

  return normalized
}

type NormalizeShippingInfoOptionsType = {
  preferLegacyFields?: boolean
}

const keepLegacyVendorFields = <T extends ShippingInfoWithRuleGroupsType>(
  info: T,
  normalized: T & WithPurchaseTermsArraysType,
): T & WithPurchaseTermsArraysType => ({
  ...normalized,
  order_requirement: info.order_requirement,
  moq_qty: info.moq_qty,
  moq_multiplier: info.moq_multiplier,
  moq_uom: info.moq_uom,
  moq_max: info.moq_max,
  mov: info.mov,
  shipping_fee_rules: info.shipping_fee_rules,
  shipping_fee_amount: info.shipping_fee_amount,
})

export const normalizeShippingInfoFromApi = <T extends ShippingInfoWithRuleGroupsType>(
  info: T,
  options?: NormalizeShippingInfoOptionsType,
): T & WithPurchaseTermsArraysType => {
  const hasLegacyBrand = hasBrandScopeData(info.brand_order_requirements)
  const hasLegacySku = hasSkuScopeData(info.sku_order_requirements)

  if (!info.rule_groups?.length) {
    return withDefaultPurchaseTermsArrays(info)
  }

  const mappedFromRuleGroups = mapRuleGroupsToPurchaseTerms(info)
  const legacyBrandRequirements = info.brand_order_requirements ?? EMPTY_BRAND_REQUIREMENTS
  const legacySkuRequirements = info.sku_order_requirements ?? EMPTY_SKU_REQUIREMENTS

  const preferLegacyFields = options?.preferLegacyFields

  const normalized = {
    ...mappedFromRuleGroups,
    brand_order_requirements: resolveBrandOrderRequirements(
      legacyBrandRequirements,
      mappedFromRuleGroups.brand_order_requirements,
      hasLegacyBrand,
      preferLegacyFields,
    ),
    sku_order_requirements: resolveSkuOrderRequirements(
      legacySkuRequirements,
      mappedFromRuleGroups.sku_order_requirements,
      hasLegacySku,
      preferLegacyFields,
    ),
  }

  if (options?.preferLegacyFields) {
    return keepLegacyVendorFields(info, normalized)
  }

  return normalized
}

export const normalizeShippingInfoPurchaseTerms = <T extends ShippingInfoWithRuleGroupsType>(
  info: T,
  options?: NormalizeShippingInfoOptionsType,
): T & WithPurchaseTermsArraysType => {
  const normalized = normalizeShippingInfoFromApi(info, options)

  return {
    ...normalized,
    mov: movToString(normalized.mov),
    shipping_fee_amount:
      normalized.shipping_fee_amount == null || normalized.shipping_fee_amount === ''
        ? '0'
        : String(normalized.shipping_fee_amount),
    brand_order_requirements: enrichBrandRequirementIdsFromRuleGroups(
      hydrateBrandOrderRequirements(normalized.brand_order_requirements),
      getActiveBrandRuleGroups(normalized.rule_groups),
    ),
    sku_order_requirements: enrichSkuRequirementIdsFromRuleGroups(
      normalized.sku_order_requirements ?? EMPTY_SKU_REQUIREMENTS,
      getActiveSkuRuleGroups(normalized.rule_groups),
    ),
  }
}

export const normalizeVendorShippingInformation = <T extends ShippingInfoWithRuleGroupsType>(
  shippingInformation: T[] | undefined,
): T[] => (shippingInformation ?? []).map((info) => normalizeShippingInfoPurchaseTerms(info))

export const normalizeShippingInfoForComparison = <T extends ShippingInfoWithRuleGroupsType>(
  info: T,
  options?: NormalizeShippingInfoOptionsType,
): T & WithPurchaseTermsArraysType => {
  const normalized = normalizeShippingInfoPurchaseTerms(info, options)

  return {
    ...normalized,
    moq_uom: resolveMoqUomSelectValue(normalized.moq_uom),
    moq_multiplier: resolveMoqMultiplierForVendor(
      normalized.moq_max ?? false,
      normalized.moq_multiplier,
    ),
    mov:
      normalized.mov != null && String(normalized.mov).trim() !== '' ? String(normalized.mov) : '0',
  }
}

export const normalizeVendorDetailsForComparison = (
  vendorDetails: VendorDetailsInterface,
  options?: NormalizeShippingInfoOptionsType,
): VendorDetailsInterface => ({
  ...vendorDetails,
  shipping_information: (vendorDetails.shipping_information ?? []).map((info) =>
    normalizeShippingInfoForComparison(info, options),
  ),
})

export const normalizeVendorDetailsFromApi = (
  vendorDetails: VendorDetailsInterface,
): VendorDetailsInterface => ({
  ...vendorDetails,
  shipping_information: normalizeVendorShippingInformation(vendorDetails.shipping_information),
})

const buildConfigFromScopeSelection = (
  brand: PurchaseTermsNamedItemType | null,
  l1Category: RuleGroupInactiveFilterItemType | null,
  l2Category: RuleGroupInactiveFilterItemType | null,
): RuleGroupConfigType | null => {
  const config: RuleGroupConfigType = {
    id: 0,
    brand_id: brand?.id && brand.id > 0 ? brand.id : 0,
    l1_category_id: l1Category?.id && l1Category.id > 0 ? l1Category.id : 0,
    l2_category_id: l2Category?.id && l2Category.id > 0 ? l2Category.id : 0,
  }

  if (brand?.id && brand.id > 0) {
    config.brand_name = brand.name
  }
  if (l1Category?.id && l1Category.id > 0) {
    config.l1_category_name = l1Category.name
  }
  if (l2Category?.id && l2Category.id > 0) {
    config.l2_category_name = l2Category.name
  }

  if (!config.brand_id && !config.l1_category_id && !config.l2_category_id) {
    return null
  }

  return config
}

export const toConfigSubmissionPayload = (config: RuleGroupConfigType): RuleGroupConfigType => {
  const payload: RuleGroupConfigType = {
    id: config.id ?? 0,
    brand_id: config.brand_id ?? 0,
    l1_category_id: config.l1_category_id ?? 0,
    l2_category_id: config.l2_category_id ?? 0,
  }
  if (config.marked_for_deletion === true) {
    payload.marked_for_deletion = true
  }
  return payload
}

const expandBrandScopeToConfigs = (
  requirement: BrandOrderRequirementType,
): RuleGroupConfigType[] => {
  if (requirement.rule_group_configs?.length) {
    return requirement.rule_group_configs
  }

  const brandList = requirement.brands.length > 0 ? requirement.brands : [null]
  const l1List = requirement.l1Categories.length > 0 ? requirement.l1Categories : [null]
  const l2List = requirement.l2Categories.length > 0 ? requirement.l2Categories : [null]
  const configs: RuleGroupConfigType[] = []

  brandList.forEach((brand) => {
    l1List.forEach((l1Category) => {
      l2List.forEach((l2Category) => {
        const config = buildConfigFromScopeSelection(brand, l1Category, l2Category)
        if (config) {
          configs.push(config)
        }
      })
    })
  })

  return configs
}

export const resolveBrandScopeConfigsForProductList = (
  requirement: BrandOrderRequirementType,
): RuleGroupConfigType[] =>
  expandBrandScopeToConfigs(requirement).filter((config) => config.marked_for_deletion !== true)

const buildBrandScopeConfigFingerprint = (configs: RuleGroupConfigType[]): string => {
  const scopeKeys = configs
    .filter((config) => config.marked_for_deletion !== true)
    .map(
      (config) =>
        `${config.brand_id ?? 0}:${config.l1_category_id ?? 0}:${config.l2_category_id ?? 0}`,
    )
    .sort()

  return scopeKeys.join('|')
}

const buildBrandRequirementScopeFingerprint = (requirement: BrandOrderRequirementType): string => {
  const configs = requirement.rule_group_configs?.length
    ? requirement.rule_group_configs
    : expandBrandScopeToConfigs(requirement)

  return buildBrandScopeConfigFingerprint(configs)
}

const buildSkuProductIdsFingerprint = (productIds: number[]): string =>
  collectUniqueIds(productIds)
    .sort((first, second) => first - second)
    .join(',')

const collectSkuRequirementProductIds = (requirement: SkuOrderRequirementType): number[] =>
  collectUniqueIds([
    ...(requirement.products ?? []).map((product) => product.id),
    ...(requirement.rule_group_products ?? []).map((product) => product.product_id),
  ])

const collectSkuRuleGroupProductIds = (group: RuleGroupType): number[] => {
  if (group.product_ids?.length) {
    return collectUniqueIds(group.product_ids)
  }

  return collectUniqueIds((group.products ?? []).map((product) => product.product_id ?? product.id))
}

const findUniqueMatchingRuleGroupId = (
  ruleGroups: RuleGroupType[],
  claimedIds: Set<number>,
  matchesGroup: (group: RuleGroupType) => boolean,
): number | undefined => {
  const matches = ruleGroups.filter((group) => {
    const groupId = group.id
    if (!groupId || groupId <= 0 || claimedIds.has(groupId)) return false
    return matchesGroup(group)
  })

  if (matches.length !== 1) return undefined

  const matchedId = matches[0]?.id
  return matchedId && matchedId > 0 ? matchedId : undefined
}

const enrichBrandRequirementIdsFromRuleGroups = (
  requirements: BrandOrderRequirementType[],
  ruleGroups: RuleGroupType[],
): BrandOrderRequirementType[] => {
  const claimedIds = new Set(
    requirements
      .map((requirement) => requirement.id)
      .filter((id): id is number => typeof id === 'number' && id > 0),
  )

  return requirements.map((requirement) => {
    if (requirement.marked_for_deletion) return requirement
    if (requirement.id && requirement.id > 0) return requirement

    const requirementFingerprint = buildBrandRequirementScopeFingerprint(requirement)
    const groupId = findUniqueMatchingRuleGroupId(
      ruleGroups,
      claimedIds,
      (group) => buildBrandScopeConfigFingerprint(group.configs ?? []) === requirementFingerprint,
    )

    if (!groupId) return requirement

    claimedIds.add(groupId)
    return { ...requirement, id: groupId }
  })
}

const enrichSkuRequirementIdsFromRuleGroups = (
  requirements: SkuOrderRequirementType[],
  ruleGroups: RuleGroupType[],
): SkuOrderRequirementType[] => {
  const claimedIds = new Set(
    requirements
      .map((requirement) => requirement.id)
      .filter((id): id is number => typeof id === 'number' && id > 0),
  )

  return requirements.map((requirement) => {
    if (requirement.marked_for_deletion) return requirement
    if (requirement.id && requirement.id > 0) return requirement

    const requirementFingerprint = buildSkuProductIdsFingerprint(
      collectSkuRequirementProductIds(requirement),
    )
    const groupId = findUniqueMatchingRuleGroupId(
      ruleGroups,
      claimedIds,
      (group) =>
        buildSkuProductIdsFingerprint(collectSkuRuleGroupProductIds(group)) ===
        requirementFingerprint,
    )

    if (!groupId) return requirement

    claimedIds.add(groupId)
    return { ...requirement, id: groupId }
  })
}

const resolveMergedNotes = (existing: RuleGroupType, built: RuleGroupType): string => {
  if (built.level === RuleGroupLevelEnum.VENDOR) {
    return built.notes?.trim() ? built.notes : existing.notes ?? built.notes ?? ''
  }

  return built.notes ?? ''
}

const findExistingRuleGroup = (
  ruleGroups: RuleGroupType[] | undefined,
  built: RuleGroupType,
): RuleGroupType | undefined => {
  if (!ruleGroups?.length) return undefined

  if (built.id && built.id > 0) {
    return ruleGroups.find((group) => group.id === built.id && group.level === built.level)
  }

  return undefined
}

export const mergeRuleGroupWithExisting = (
  existing: RuleGroupType | undefined,
  built: RuleGroupType,
): RuleGroupType => {
  if (!existing) return built

  const mergedNotes = resolveMergedNotes(existing, built)

  const mergedConfigs = built.configs?.length ? built.configs : existing.configs ?? built.configs

  const mergedProductIds = built.product_ids?.length
    ? built.product_ids
    : existing.product_ids ?? built.product_ids

  if (built.level === RuleGroupLevelEnum.SKU || built.level === RuleGroupLevelEnum.BRAND) {
    const existingWithoutProducts = { ...existing }
    delete existingWithoutProducts.products
    const builtWithoutProducts = { ...built }
    delete builtWithoutProducts.products

    return {
      ...existingWithoutProducts,
      ...builtWithoutProducts,
      last_validated_at: existing.last_validated_at ?? built.last_validated_at,
      inactive_filters: existing.inactive_filters ?? built.inactive_filters,
      configs: mergedConfigs,
      product_ids: mergedProductIds,
      notes: mergedNotes,
    }
  }

  return {
    ...existing,
    ...built,
    last_validated_at: existing.last_validated_at ?? built.last_validated_at,
    inactive_filters: existing.inactive_filters ?? built.inactive_filters,
    configs: mergedConfigs,
    product_ids: mergedProductIds,
    notes: mergedNotes,
  }
}

const buildVendorRuleGroup = (
  info: ShippingInfoRuleGroupSourceType,
  existingRuleGroups?: RuleGroupType[],
): RuleGroupType => {
  const sanitized = sanitizeMoqMovFieldsByOrderRequirement(info)

  const built: RuleGroupType = {
    id: resolveVendorRuleGroupIdFromApi(info),
    level: RuleGroupLevelEnum.VENDOR,
    order_requirement:
      (sanitized.order_requirement as RuleGroupType['order_requirement']) ?? 'None',
    moq_qty: sanitized.moq_qty ?? 0,
    moq_multiplier: resolveMoqMultiplierForVendor(
      sanitized.moq_max ?? false,
      sanitized.moq_multiplier,
    ),
    moq_uom: sanitized.moq_uom ?? '',
    mov: movToNumber(sanitized.mov),
    notes: '',
    is_active: true,
    marked_for_deletion: false,
    shipping_fee_rules: info.shipping_fee_rules ?? false,
    shipping_fee_amount: info.shipping_fee_amount ?? '0',
  }

  const existing = findExistingRuleGroup(existingRuleGroups, built)
  return mergeRuleGroupWithExisting(existing, built)
}

const buildBrandRuleGroup = (
  requirement: BrandOrderRequirementType,
  existingRuleGroups?: RuleGroupType[],
): RuleGroupType => {
  const sanitized = sanitizeMoqMovFieldsByOrderRequirement({
    order_requirement: requirement.order_requirement,
    moq_qty: requirement.moq_qty,
    moq_uom: requirement.moq_uom,
    moq_multiplier: requirement.moq_multiplier,
    mov: requirement.mov,
  })

  const configsFromRequirement = requirement.rule_group_configs?.length
    ? requirement.rule_group_configs.map(toConfigSubmissionPayload)
    : expandBrandScopeToConfigs(requirement).map(toConfigSubmissionPayload)

  const built: RuleGroupType = {
    id: requirement.id && requirement.id > 0 ? requirement.id : 0,
    level: RuleGroupLevelEnum.BRAND,
    order_requirement: requirement.order_requirement,
    moq_qty: sanitized.moq_qty ?? 0,
    moq_multiplier: resolveMoqMultiplierSubmitValue(sanitized.moq_multiplier),
    moq_uom: sanitized.moq_uom ?? '',
    mov: movToNumber(sanitized.mov),
    notes: requirement.notes ?? '',
    is_active: !requirement.is_inactive,
    marked_for_deletion: requirement.marked_for_deletion ?? false,
    configs: configsFromRequirement,
    inactive_filters: requirement.inactive_filters,
    last_validated_at: requirement.last_validated_at,
  }

  const existing = findExistingRuleGroup(existingRuleGroups, built)
  return mergeRuleGroupWithExisting(existing, built)
}

const buildSkuRuleGroup = (
  requirement: SkuOrderRequirementType,
  existingRuleGroups?: RuleGroupType[],
): RuleGroupType => {
  const sanitized = sanitizeMoqMovFieldsByOrderRequirement({
    order_requirement: requirement.order_requirement,
    moq_qty: requirement.moq_qty,
    moq_uom: requirement.moq_uom,
    moq_multiplier: requirement.moq_multiplier,
    mov: requirement.mov,
  })

  const ruleGroupProducts = requirement.rule_group_products ?? []
  const productIdsFromProducts = collectUniqueIds(
    (requirement.products ?? []).map((product) => product.id),
  )
  const productIdsFromRuleGroupProducts = collectUniqueIds(
    ruleGroupProducts.map((product) => product.product_id),
  )
  const productIds = productIdsFromProducts.length
    ? productIdsFromProducts
    : productIdsFromRuleGroupProducts

  const built: RuleGroupType = {
    id: requirement.id && requirement.id > 0 ? requirement.id : 0,
    level: RuleGroupLevelEnum.SKU,
    order_requirement: requirement.order_requirement,
    moq_qty: sanitized.moq_qty ?? 0,
    moq_multiplier: resolveMoqMultiplierSubmitValue(sanitized.moq_multiplier),
    moq_uom: sanitized.moq_uom ?? '',
    mov: movToNumber(sanitized.mov),
    notes: requirement.notes ?? '',
    is_active: !requirement.is_inactive,
    marked_for_deletion: requirement.marked_for_deletion ?? false,
    last_validated_at: requirement.last_validated_at,
    product_ids: productIds,
  }

  const existing = findExistingRuleGroup(existingRuleGroups, built)
  return mergeRuleGroupWithExisting(existing, built)
}

export const buildSubmissionShippingInfoItem = <T extends ShippingInfoRuleGroupSourceType>(
  info: T,
): T & {
  mov: string
  moq_max: boolean
  use_rule_groups: boolean
  rule_groups: RuleGroupType[]
} => {
  const sanitizedInfo = sanitizeMoqMovFieldsByOrderRequirement(info)
  const brandRequirements = filterOrderRequirementsForSubmission(
    sanitizedInfo.brand_order_requirements,
  )
  const skuRequirements = filterOrderRequirementsForSubmission(sanitizedInfo.sku_order_requirements)

  const existingRuleGroups = sanitizedInfo.rule_groups

  const ruleGroups: RuleGroupType[] = [
    buildVendorRuleGroup(sanitizedInfo, existingRuleGroups),
    ...brandRequirements.map((requirement) => buildBrandRuleGroup(requirement, existingRuleGroups)),
    ...skuRequirements.map((requirement) => buildSkuRuleGroup(requirement, existingRuleGroups)),
  ]

  const useRuleGroups =
    brandRequirements.length > 0 ||
    skuRequirements.length > 0 ||
    sanitizedInfo.use_rule_groups === true

  return {
    ...sanitizedInfo,
    brand_order_requirements: brandRequirements,
    sku_order_requirements: skuRequirements,
    mov: sanitizedInfo.mov || '0',
    moq_max: sanitizedInfo.moq_max ?? false,
    moq_multiplier: resolveMoqMultiplierForVendor(
      sanitizedInfo.moq_max ?? false,
      sanitizedInfo.moq_multiplier,
    ),
    use_rule_groups: useRuleGroups,
    rule_groups: ruleGroups,
  }
}

export const buildSubmissionShippingInformation = <T extends ShippingInfoRuleGroupSourceType>(
  shippingInformation: T[] | undefined,
) => (shippingInformation ?? []).map(buildSubmissionShippingInfoItem)
